import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Smartphone, Zap, Wifi, Gamepad2, Heart, DollarSign, TrendingUp, Settings, Loader2, Save, Tv, CreditCard, Phone, Music, ShoppingBag, Globe, Search, CheckCircle, Play, RefreshCw, Eye, AlertCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';

const categoryIcons = {
  pulsa: Smartphone,
  data: Wifi,
  pln: Zap,
  game: Gamepad2,
  bpjs: Heart,
  emoney: CreditCard,
  'e-money': CreditCard,
  tv: Tv,
  'tv kabel': Tv,
  streaming: Music,
  voucher: ShoppingBag,
  wifi: Globe,
  sms: Phone,
  transfer: DollarSign,
  telkom: Phone,
  pdam: Zap,
};

export default function TenantPPOB() {
  const [settings, setSettings] = useState({ provider: 'digiflazz', is_active: false, markup_type: 'fixed', markup_value: 500 });
  const [markups, setMarkups] = useState([]);
  const [callbackUrl, setCallbackUrl] = useState('');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [apiCategories, setApiCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editMarkup, setEditMarkup] = useState({ category: '', markup_type: 'fixed', markup_value: 500 });
  const [processingOrderId, setProcessingOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchData = async () => {
    try {
      const [settingsRes, productsRes, ordersRes, statsRes] = await Promise.all([
        api.get('/tenant/ppob/settings'),
        api.get('/tenant/ppob/products'),
        api.get('/tenant/ppob/orders'),
        api.get('/tenant/ppob/stats')
      ]);
      setSettings(settingsRes.data.settings || {});
      setMarkups(settingsRes.data.markups || []);
      setCallbackUrl(settingsRes.data.callback_url || '');
      setProducts(productsRes.data.products || []);
      setOrders(ordersRes.data.orders || []);
      setStats(statsRes.data);
      // Fetch categories
      try {
        const catRes = await api.get('/tenant/ppob/categories');
        setApiCategories(catRes.data.categories || []);
      } catch (e) {}
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Poll order status after processing - checks every 3s for up to 60s
  const pollOrderStatus = async (orderId) => {
    setProcessingOrderId(orderId);
    const maxPolls = 20; // 20 x 3s = 60s max
    for (let i = 0; i < maxPolls; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await api.get(`/tenant/ppob/orders/${orderId}/status`);
        const st = res.data;
        if (st.status === 'success') {
          toast.success(`PPOB Berhasil! ${st.sn ? 'SN: ' + st.sn : ''}`, { duration: 8000 });
          setProcessingOrderId(null);
          const ordRes = await api.get('/tenant/ppob/orders');
          setOrders(ordRes.data.orders || []);
          fetchData(); // refresh stats
          return;
        }
        if (st.status === 'failed') {
          toast.error(`PPOB Gagal: ${st.callback_message || st.tripay_message || 'Transaksi gagal'}`, { duration: 8000 });
          setProcessingOrderId(null);
          const ordRes = await api.get('/tenant/ppob/orders');
          setOrders(ordRes.data.orders || []);
          return;
        }
        // Still processing, continue polling
      } catch {
        // ignore poll errors, keep trying
      }
    }
    // Timeout - still processing after 60s
    toast.info('Transaksi masih diproses Tripay. Status akan update otomatis via callback.', { duration: 8000 });
    setProcessingOrderId(null);
    const ordRes = await api.get('/tenant/ppob/orders');
    setOrders(ordRes.data.orders || []);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/ppob/settings', settings);
      toast.success('Settings disimpan');
      setShowSettingsDialog(false);
      fetchData();
    } catch (e) {
      toast.error('Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMarkup = async () => {
    if (!editMarkup.category) return;
    try {
      await api.put('/tenant/ppob/markup', editMarkup);
      toast.success('Markup diperbarui');
      setEditMarkup({ category: '', markup_type: 'fixed', markup_value: 500 });
      fetchData();
    } catch (e) {
      toast.error('Gagal menyimpan markup');
    }
  };

  const getMarkupForCategory = (cat) => {
    const m = markups.find(mk => mk.category === cat);
    return m || { markup_type: settings.markup_type, markup_value: settings.markup_value };
  };

  const filteredProducts = products.filter(p => {
    if (selectedCategory && p.category !== selectedCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (p.name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q) || (p.brand || '').toLowerCase().includes(q);
    }
    return true;
  });

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" />
      </div>
    );
  }

  return (
    <div data-testid="tenant-ppob-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>PPOB</h1>
          <p className="text-sm text-slate-500">Jual pulsa, paket data, token PLN, dan lainnya</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={settings.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
            {settings.is_active ? 'Active' : 'Inactive'}
          </Badge>
          <Button variant="outline" onClick={() => setShowSettingsDialog(true)}>
            <Settings size={16} className="mr-2" /> Settings
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Smartphone size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_orders || 0}</p>
                <p className="text-xs text-slate-500">Total Transaksi</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <TrendingUp size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.success_rate || 0}%</p>
                <p className="text-xs text-slate-500">Success Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <DollarSign size={20} className="text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">Rp {fmt(stats.total_revenue)}</p>
                <p className="text-xs text-slate-500">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <DollarSign size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">Rp {fmt(stats.total_profit)}</p>
                <p className="text-xs text-slate-500">Total Profit</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {!settings.is_active && (
        <Card className="border-amber-200 bg-amber-50 mb-6">
          <CardContent className="p-4 flex items-center gap-3">
            <Settings size={20} className="text-amber-600" />
            <p className="text-amber-800">PPOB belum aktif. Klik Settings untuk mengaktifkan dan mengatur API credentials.</p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products">Produk ({products.length})</TabsTrigger>
          <TabsTrigger value="orders">Transaksi ({orders.length})</TabsTrigger>
          <TabsTrigger value="markup">Markup</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          {/* Search */}
          <div className="relative mb-4 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cari produk..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          {/* Category Filter */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <Button variant={!selectedCategory ? 'default' : 'outline'} size="sm" onClick={() => setSelectedCategory('')}>
              Semua
            </Button>
            {categories.map(cat => {
              const Icon = categoryIcons[cat] || Smartphone;
              return (
                <Button key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} size="sm" onClick={() => setSelectedCategory(cat)}>
                  <Icon size={14} className="mr-1" /> {cat}
                </Button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filteredProducts.map((p) => {
              const Icon = categoryIcons[p.category] || Smartphone;
              return (
                <Card key={p.sku} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={16} className="text-[#2D6CDF]" />
                      <Badge variant="outline" className="text-xs">{p.brand}</Badge>
                    </div>
                    <p className="font-medium text-slate-900 text-sm line-clamp-1">{p.name}</p>
                    <div className="mt-2">
                      <p className="text-xs text-slate-500 line-through">Rp {fmt(p.price)}</p>
                      <p className="font-bold text-[#2D6CDF]">Rp {fmt(p.selling_price)}</p>
                      <p className="text-xs text-emerald-600">+Rp {fmt(p.profit)} profit</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {products.length === 0 && (
            <div className="py-12 text-center text-slate-400">
              <Smartphone size={48} className="mx-auto mb-4 text-slate-300" />
              <p>Produk PPOB akan muncul setelah PPOB diaktifkan</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Produk</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Customer</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Harga Jual</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Profit</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Pembayaran</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const payLabel = { qris: 'QRIS', va: 'VA', ewallet: 'E-Wallet', bank_transfer: 'Transfer' }[o.payment_method] || o.payment_method || '-';
                    const payStatus = o.payment_status || 'unpaid';
                    return (
                      <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{o.product_name}</p>
                          <p className="text-xs text-slate-500">{o.sku}</p>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-slate-900">{o.customer_number}</p>
                          <p className="text-xs text-slate-500">{o.customer_name}</p>
                        </td>
                        <td className="py-3 px-4 font-medium">Rp {fmt(o.selling_price)}</td>
                        <td className="py-3 px-4 font-medium text-emerald-600">+Rp {fmt(o.profit)}</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-slate-500">{payLabel}</span>
                            <Badge variant="outline" className={
                              payStatus === 'paid' ? 'bg-emerald-100 text-emerald-700 text-xs' :
                              payStatus === 'awaiting_payment' ? 'bg-blue-100 text-blue-700 text-xs' :
                              'bg-slate-100 text-slate-600 text-xs'
                            }>
                              {payStatus === 'paid' ? 'Lunas' : payStatus === 'awaiting_payment' ? 'Menunggu' : 'Belum Bayar'}
                            </Badge>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className={
                            o.status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                            o.status === 'failed' ? 'bg-red-100 text-red-700' :
                            o.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }>{o.status === 'success' ? 'Berhasil' : o.status === 'failed' ? 'Gagal' : o.status === 'processing' ? 'Diproses' : 'Pending'}</Badge>
                          {o.sn && <p className="text-xs text-emerald-600 mt-1 font-mono">SN: {o.sn}</p>}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-1.5">
                            {(o.status === 'pending_payment' || o.status === 'pending') && o.payment_status !== 'paid' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7 border-blue-200 text-blue-700 hover:bg-blue-50"
                                onClick={async () => {
                                  try {
                                    await api.post(`/tenant/ppob/orders/${o.id}/confirm-payment`);
                                    toast.success('Pembayaran dikonfirmasi');
                                    const res = await api.get('/tenant/ppob/orders');
                                    setOrders(res.data.orders || []);
                                  } catch (e) { toast.error(e.response?.data?.detail || 'Gagal konfirmasi'); }
                                }}
                              >
                                <CheckCircle size={12} className="mr-1" /> Konfirmasi Bayar
                              </Button>
                            )}
                            {(o.status === 'pending_payment' || o.status === 'pending' || o.status === 'failed') && o.payment_status === 'paid' && (
                              <Button
                                size="sm"
                                className="text-xs h-7 bg-indigo-600 hover:bg-indigo-700 text-white"
                                disabled={processingOrderId === o.id}
                                onClick={async () => {
                                  try {
                                    setProcessingOrderId(o.id);
                                    const res = await api.post(`/tenant/ppob/orders/${o.id}/process`);
                                    if (res.data.status === 'success') {
                                      // Instant success (SN returned immediately)
                                      toast.success(`PPOB Berhasil! ${res.data.sn ? 'SN: ' + res.data.sn : ''}`, { duration: 8000 });
                                      setProcessingOrderId(null);
                                      const ordRes = await api.get('/tenant/ppob/orders');
                                      setOrders(ordRes.data.orders || []);
                                      fetchData();
                                    } else {
                                      // Processing - poll for realtime status from Tripay callback
                                      toast.info('Menunggu konfirmasi dari Tripay...', { duration: 5000 });
                                      const ordRes = await api.get('/tenant/ppob/orders');
                                      setOrders(ordRes.data.orders || []);
                                      pollOrderStatus(o.id);
                                    }
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Gagal memproses PPOB');
                                    setProcessingOrderId(null);
                                    const ordRes = await api.get('/tenant/ppob/orders');
                                    setOrders(ordRes.data.orders || []);
                                  }
                                }}
                              >
                                {processingOrderId === o.id ? (
                                  <><Loader2 size={12} className="mr-1 animate-spin" /> Menunggu Tripay...</>
                                ) : (
                                  <><Play size={12} className="mr-1" /> {o.status === 'failed' ? 'Retry PPOB' : 'Proses PPOB'}</>
                                )}
                              </Button>
                            )}
                            {o.status === 'processing' && processingOrderId !== o.id && (
                              <div className="flex flex-col gap-1.5">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs h-7 border-blue-200 text-blue-700 hover:bg-blue-50"
                                  onClick={() => {
                                    toast.info('Menunggu konfirmasi dari Tripay...');
                                    pollOrderStatus(o.id);
                                  }}
                                >
                                  <RefreshCw size={12} className="mr-1" /> Cek Status
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs h-7 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                  onClick={async () => {
                                    try {
                                      await api.post(`/tenant/ppob/orders/${o.id}/mark-success`);
                                      toast.success('Ditandai berhasil');
                                      const ordRes = await api.get('/tenant/ppob/orders');
                                      setOrders(ordRes.data.orders || []);
                                    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
                                  }}
                                >
                                  <CheckCircle size={12} className="mr-1" /> Tandai Berhasil
                                </Button>
                              </div>
                            )}
                            {o.status === 'processing' && processingOrderId === o.id && (
                              <div className="flex items-center gap-1.5 text-blue-600">
                                <Loader2 size={14} className="animate-spin" />
                                <span className="text-xs font-medium">Menunggu Tripay...</span>
                              </div>
                            )}
                            {o.status === 'success' && (
                              <span className="text-xs text-emerald-600 font-medium">Selesai</span>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-xs h-7 text-slate-500 hover:text-slate-700"
                              onClick={() => setSelectedOrder(o)}
                            >
                              <Eye size={12} className="mr-1" /> Detail
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {orders.length === 0 && (
                    <tr><td colSpan={7} className="py-12 text-center text-slate-400">Belum ada transaksi PPOB</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="markup" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Default Markup</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Markup default untuk semua kategori</p>
                  <p className="text-2xl font-bold text-[#2D6CDF] mt-1">
                    {settings.markup_type === 'percentage' ? `${settings.markup_value}%` : `Rp ${fmt(settings.markup_value)}`}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Set Markup per Kategori</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Kategori</Label>
                  <Select value={editMarkup.category} onValueChange={(v) => setEditMarkup({ ...editMarkup, category: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih kategori" />
                    </SelectTrigger>
                    <SelectContent>
                      {(apiCategories.length > 0 ? apiCategories : [{slug:'pulsa',name:'Pulsa'},{slug:'data',name:'Data'},{slug:'pln',name:'PLN'},{slug:'bpjs',name:'BPJS'},{slug:'game',name:'Game'},{slug:'emoney',name:'E-Money'},{slug:'streaming',name:'Streaming'},{slug:'voucher',name:'Voucher'}]).map(cat => (
                        <SelectItem key={cat.slug || cat.name} value={cat.slug || cat.name.toLowerCase()}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipe</Label>
                    <Select value={editMarkup.markup_type} onValueChange={(v) => setEditMarkup({ ...editMarkup, markup_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed (Rp)</SelectItem>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nilai</Label>
                    <Input type="number" value={editMarkup.markup_value} onChange={(e) => setEditMarkup({ ...editMarkup, markup_value: parseInt(e.target.value) || 0 })} />
                  </div>
                </div>
                <Button onClick={handleSaveMarkup} disabled={!editMarkup.category} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]">
                  <Save size={14} className="mr-2" /> Simpan Markup
                </Button>
              </CardContent>
            </Card>

            {/* Existing Markups */}
            <Card className="border-slate-200 shadow-sm md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Markup Aktif</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-wrap">
                  {(apiCategories.length > 0 ? apiCategories.map(c => c.slug || c.name.toLowerCase()) : ['pulsa', 'data', 'pln', 'bpjs', 'game', 'emoney', 'streaming', 'voucher']).map(cat => {
                    const Icon = categoryIcons[cat] || Smartphone;
                    const isCustom = markups.some(mk => mk.category === cat);
                    return (
                      <div key={cat} className={`p-3 rounded-lg border ${isCustom ? 'border-[#2D6CDF] bg-blue-50' : 'border-slate-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Icon size={16} className="text-slate-600" />
                          <span className="font-medium capitalize text-sm">{cat}</span>
                        </div>
                        <p className="text-lg font-bold text-slate-900">
                          {(getMarkupForCategory(cat)).markup_type === 'percentage' ? `${(getMarkupForCategory(cat)).markup_value}%` : `Rp ${fmt((getMarkupForCategory(cat)).markup_value)}`}
                        </p>
                        {isCustom && <Badge className="mt-1 bg-[#2D6CDF]/10 text-[#2D6CDF] text-xs">Custom</Badge>}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>PPOB Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Aktifkan PPOB</Label>
              <Switch checked={settings.is_active} onCheckedChange={(v) => setSettings({ ...settings, is_active: v })} />
            </div>
            <div>
              <Label>Provider</Label>
              <Select value={settings.provider} onValueChange={(v) => setSettings({ ...settings, provider: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="digiflazz">Digiflazz</SelectItem>
                  <SelectItem value="tripay">Tripay</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {settings.provider === 'digiflazz' && (
              <>
                <div>
                  <Label>Digiflazz Username</Label>
                  <Input value={settings.digiflazz_username || ''} onChange={(e) => setSettings({ ...settings, digiflazz_username: e.target.value })} placeholder="Username Digiflazz" />
                </div>
                <div>
                  <Label>Digiflazz API Key</Label>
                  <Input type="password" value={settings.digiflazz_api_key || ''} onChange={(e) => setSettings({ ...settings, digiflazz_api_key: e.target.value })} placeholder="API Key" />
                </div>
              </>
            )}
            {settings.provider === 'tripay' && (
              <>
                <div>
                  <Label>Tripay API Key</Label>
                  <Input type="password" value={settings.tripay_api_key || ''} onChange={(e) => setSettings({ ...settings, tripay_api_key: e.target.value })} placeholder="API Key dari tripay.id" />
                  <p className="text-xs text-slate-500 mt-1">Dapatkan di tripay.id → Menu API</p>
                </div>
                <div>
                  <Label>Tripay PIN</Label>
                  <Input type="password" value={settings.tripay_pin || ''} onChange={(e) => setSettings({ ...settings, tripay_pin: e.target.value })} placeholder="PIN Transaksi" />
                  <p className="text-xs text-slate-500 mt-1">PIN untuk otorisasi transaksi pembelian</p>
                </div>
                <div>
                  <Label>Tripay Private Key</Label>
                  <Input type="password" value={settings.tripay_private_key || ''} onChange={(e) => setSettings({ ...settings, tripay_private_key: e.target.value })} placeholder="Private Key (opsional)" />
                </div>
                <div>
                  <Label>Tripay Merchant Code</Label>
                  <Input value={settings.tripay_merchant_code || ''} onChange={(e) => setSettings({ ...settings, tripay_merchant_code: e.target.value })} placeholder="Merchant Code (opsional)" />
                </div>
                {callbackUrl && (
                  <div className="bg-slate-50 p-3 rounded border">
                    <Label className="text-xs font-semibold">Callback URL</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input value={callbackUrl} readOnly className="text-xs bg-white font-mono" />
                      <Button type="button" variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(callbackUrl); }}>
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Masukkan URL ini di dashboard Tripay → Pengaturan → Callback URL PPOB</p>
                  </div>
                )}
              </>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Default Markup Type</Label>
                <Select value={settings.markup_type} onValueChange={(v) => setSettings({ ...settings, markup_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed (Rp)</SelectItem>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default Markup Value</Label>
                <Input type="number" value={settings.markup_value} onChange={(e) => setSettings({ ...settings, markup_value: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            {(!settings.tripay_api_key && settings.provider === 'tripay') && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ API Key belum diisi. Produk dan transaksi menggunakan data demo (sandbox mode).
              </p>
            )}
            {(settings.tripay_api_key && settings.provider === 'tripay' && !settings.tripay_pin) && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ PIN belum diisi. Produk bisa ditampilkan tapi transaksi pembelian tidak bisa diproses.
              </p>
            )}
            {(settings.tripay_api_key && settings.tripay_pin && settings.provider === 'tripay') && (
              <p className="text-xs text-green-600 bg-green-50 p-2 rounded">
                ✅ Tripay PPOB terkonfigurasi. Produk dan transaksi menggunakan API live.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Batal</Button>
            <Button onClick={handleSaveSettings} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]">
              {saving && <Loader2 size={14} className="animate-spin mr-2" />}
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={(v) => !v && setSelectedOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedOrder?.status === 'failed' && <AlertCircle size={18} className="text-red-500" />}
              {selectedOrder?.status === 'success' && <CheckCircle size={18} className="text-emerald-500" />}
              {selectedOrder?.status === 'processing' && <Loader2 size={18} className="text-blue-500 animate-spin" />}
              Detail Transaksi
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              {/* Status Banner */}
              <div className={`p-3 rounded-lg ${
                selectedOrder.status === 'failed' ? 'bg-red-50 border border-red-200' :
                selectedOrder.status === 'success' ? 'bg-emerald-50 border border-emerald-200' :
                selectedOrder.status === 'processing' ? 'bg-blue-50 border border-blue-200' :
                'bg-amber-50 border border-amber-200'
              }`}>
                <p className={`text-sm font-semibold ${
                  selectedOrder.status === 'failed' ? 'text-red-700' :
                  selectedOrder.status === 'success' ? 'text-emerald-700' :
                  selectedOrder.status === 'processing' ? 'text-blue-700' :
                  'text-amber-700'
                }`}>
                  {selectedOrder.status === 'failed' ? 'Transaksi Gagal' :
                   selectedOrder.status === 'success' ? 'Transaksi Berhasil' :
                   selectedOrder.status === 'processing' ? 'Sedang Diproses' : 'Menunggu'}
                </p>
                {(selectedOrder.callback_message || selectedOrder.tripay_message) && (
                  <p className="text-sm mt-1 text-slate-700">
                    {selectedOrder.callback_message || selectedOrder.tripay_message}
                  </p>
                )}
              </div>

              {/* Order Info */}
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Produk</span>
                  <span className="font-medium text-slate-900">{selectedOrder.product_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">SKU</span>
                  <span className="font-mono text-slate-700">{selectedOrder.sku}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">No. Tujuan</span>
                  <span className="font-mono text-slate-900">{selectedOrder.customer_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Nama</span>
                  <span className="text-slate-900">{selectedOrder.customer_name || '-'}</span>
                </div>
                <hr className="border-slate-100" />
                <div className="flex justify-between">
                  <span className="text-slate-500">Harga Modal</span>
                  <span className="text-slate-700">Rp {fmt(selectedOrder.base_price || selectedOrder.price)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Harga Jual</span>
                  <span className="font-medium text-slate-900">Rp {fmt(selectedOrder.selling_price)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Profit</span>
                  <span className="font-medium text-emerald-600">+Rp {fmt(selectedOrder.profit)}</span>
                </div>
                <hr className="border-slate-100" />
                <div className="flex justify-between">
                  <span className="text-slate-500">Pembayaran</span>
                  <span className="text-slate-700">
                    {{ qris: 'QRIS', va: 'VA', ewallet: 'E-Wallet', bank_transfer: 'Transfer' }[selectedOrder.payment_method] || selectedOrder.payment_method || '-'}
                    {' '}
                    <Badge variant="outline" className={selectedOrder.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-slate-100 text-slate-600 text-xs'}>
                      {selectedOrder.payment_status === 'paid' ? 'Lunas' : 'Belum'}
                    </Badge>
                  </span>
                </div>
                {selectedOrder.provider_ref && !selectedOrder.provider_ref.startsWith('ERR-') && !selectedOrder.provider_ref.startsWith('MOCK-') && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Tripay ID</span>
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-slate-700">{selectedOrder.provider_ref}</span>
                      <button className="text-slate-400 hover:text-slate-600" onClick={() => { navigator.clipboard.writeText(selectedOrder.provider_ref); toast.success('Copied!'); }}>
                        <Copy size={12} />
                      </button>
                    </div>
                  </div>
                )}
                {selectedOrder.sn && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Serial Number</span>
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-emerald-700 font-medium">{selectedOrder.sn}</span>
                      <button className="text-slate-400 hover:text-slate-600" onClick={() => { navigator.clipboard.writeText(selectedOrder.sn); toast.success('SN copied!'); }}>
                        <Copy size={12} />
                      </button>
                    </div>
                  </div>
                )}
                {selectedOrder.order_number && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Order No</span>
                    <span className="font-mono text-xs text-slate-500">{selectedOrder.order_number}</span>
                  </div>
                )}
                {selectedOrder.created_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Dibuat</span>
                    <span className="text-slate-700 text-xs">{new Date(selectedOrder.created_at).toLocaleString('id-ID')}</span>
                  </div>
                )}
                {selectedOrder.processed_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Diproses</span>
                    <span className="text-slate-700 text-xs">{new Date(selectedOrder.processed_at).toLocaleString('id-ID')}</span>
                  </div>
                )}
              </div>

              {/* Tripay Response */}
              {(selectedOrder.tripay_message || selectedOrder.callback_message) && (
                <div className="bg-slate-50 border rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-500 mb-1">Response Tripay</p>
                  {selectedOrder.tripay_message && (
                    <p className="text-sm text-slate-700">{selectedOrder.tripay_message}</p>
                  )}
                  {selectedOrder.callback_message && selectedOrder.callback_message !== selectedOrder.tripay_message && (
                    <p className="text-sm text-slate-700 mt-1">{selectedOrder.callback_message}</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedOrder(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
